// Locally used components
import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Flex,
  Footer,
  Heading,
  Link,
  Radio,
  RadioGroup,
  Text,
  TextArea,
  TextField,
} from '@adobe/react-spectrum';
import { success } from '@react/react-spectrum/Toast';
import qs from 'query-string';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { excApi, handleApiCall } from '@exchange-frontends/api';
import { useIMS } from '@exchange-frontends/custom-hooks';

import HelpSupportGridListItem from './HelpSupportGridListItem';

/**
 * main details container
 * @memberof ReportAbuse
 * @type {styled-component}
 */

const RedText = styled.span`
  color: red;
`;

export const MESSAGES = {
  description: {
    empty: 'Description for your concern is mandatory.',
    maxLength: 'Invalid format: this field accepts a max number of 1000 characters',
  },
  category: {
    empty: 'Select an option for reporting.',
  },
};

const getInitialFormValues = () => ({
  email: { value: '', isRequired: true, id: 'email' },
  name: { value: '', isRequired: false, id: 'name' },
  category: { value: null, isRequired: true, id: 'category' },
  description: { value: '', isRequired: true, id: 'description', maxLength: 1000 },
});

const ReportAbuse = ({ label, icon, loading, appType }) => {
  const navigate = useNavigate();
  const search = useLocation().search;
  const { id: appId } = useParams();
  const section = qs.parse(search).section;
  const isDialogOpen = section === 'report-abuse';
  const [sendingReportAbuseErrored, setSendingReportAbuseErrored] = useState(false);
  const [sendingReportAbuseLoading, setSendingReportAbuseLoading] = useState(false);
  const [form, setForm] = useState(getInitialFormValues);
  const ims = useIMS();
  const profileLoading = useMemo(() => ims?.isMock || !ims?.isSignedInUser(), [ims]);
  const firstRadioRef = useRef(null);
  const descriptionInputRef = useRef(null);

  const setIsDialogOpen = (newVal) => {
    if (newVal) {
      navigate({ search: qs.stringify({ section: 'report-abuse' }) }, { replace: true });
    } else {
      navigate({ search: '' }, { replace: true });
    }
  };

  const validateField = (key, value) => {
    if (form[key].isRequired && !value) {
      return { valid: 'invalid', errorMessage: MESSAGES[key]?.empty };
    } else if (value?.length > form[key]?.maxLength) {
      return { valid: 'invalid', errorMessage: MESSAGES[key]?.maxLength };
    } else if (form[key].isRequired) {
      return { valid: 'valid' };
    } else {
      return {};
    }
  };

  const validateForm = () => {
    let result = true;
    const newForm = { ...form };

    Object.keys(form).forEach((key) => {
      const { valid, errorMessage } = validateField(key, form[key].value);
      newForm[key].valid = valid;
      newForm[key].errorMessage = errorMessage;
      if (valid === 'invalid') {
        result = false;
      }
    });
    setForm(newForm);
    return result;
  };

  const resetForm = () => {
    setIsDialogOpen(false);
    setSendingReportAbuseErrored(false);
    setSendingReportAbuseLoading(false);
    setForm(getInitialFormValues());
  };

  const onFormChange = (key, value) => {
    setForm((prev) => {
      const newState = { ...prev };
      const { valid, errorMessage } = validateField(key, value);
      newState[key].value = value;
      newState[key].valid = valid;
      newState[key].errorMessage = errorMessage;
      return newState;
    });
  };

  // pre-fill email/name with ims profile
  // otherwise redirect to sign in
  useEffect(() => {
    let mounted = true;
    const getProfileForForm = async () => {
      if (isDialogOpen) {
        if (!ims?.isMock && ims?.initialized && ims?.isSignedInUser()) {
          const { email, name } = await ims.getProfile();
          if (mounted) {
            setForm((prev) => {
              const newState = { ...prev };
              newState.email.value = email;
              newState.name.value = name;
              return newState;
            });
          }
        } else {
          ims.signIn();
        }
      }
    };
    getProfileForForm();
    return () => {
      mounted = false;
    };
  }, [ims, isDialogOpen]);

  const sendReportAbuse = async () => {
    const isValid = validateForm();
    if (!isValid) {
      // Focus logic: find the first invalid field
      if (form.category.valid === 'invalid') {
        if (firstRadioRef.current) {
          firstRadioRef.current.focus();
        }
      } else if (form.description.valid === 'invalid') {
        if (descriptionInputRef.current) {
          descriptionInputRef.current.focus();
        }
      }
      return;
    }
    setSendingReportAbuseLoading(true);
    setSendingReportAbuseErrored(false);
    try {
      const accessToken = ims.getAccessToken().token;
      const handler = handleApiCall(excApi.sendReportAbuse, {
        accessToken,
        appId,
        appType,
        description: form.description.value,
        category: form.category.value,
      });
      await handler.run();
      success('Email was successfully sent');
      resetForm();
      setIsDialogOpen(false);
    } catch (err) {
      console.log(err);
      setSendingReportAbuseErrored(true);
    } finally {
      setSendingReportAbuseLoading(false);
    }
  };

  return (
    <>
      <HelpSupportGridListItem
        icon={icon}
        label={label}
        loading={loading}
        dataLaunchId={`Help and Support : Report abuse`}
        onClick={() => setIsDialogOpen(true)}
      />
      <DialogContainer>
        {isDialogOpen && (
          <Dialog>
            <Heading level={2} marginBottom="size-50" marginTop="size-0">
              {label}
            </Heading>
            <Divider size="M" marginBottom="size-300" />
            <ButtonGroup>
              <Button variant="secondary" onPress={resetForm}>
                Cancel
              </Button>
              <Button
                autoFocus
                variant="cta"
                onPress={sendReportAbuse}
                isDisabled={profileLoading || sendingReportAbuseLoading}
                isPending={sendingReportAbuseLoading}
              >
                <Text>Report</Text>
              </Button>
            </ButtonGroup>
            <Content>
              <Text>
                If you feel that this product violates the Adobe
                <Link marginStart="size-40" variant="secondary">
                  <a href="https://www.adobe.com/legal/terms.html" target="_blank" rel="noopener noreferrer">
                    Terms of Use
                  </a>
                </Link>
                , you may report this product by filling out this quick form.
              </Text>
              <Flex width="100%" direction="column" marginTop="30px" marginBottom="10px" gap="15px">
                <TextField
                  width="100%"
                  marginBottom="5px"
                  label="Your Email"
                  required
                  type="email"
                  id="email"
                  isDisabled={profileLoading}
                  isRequired={form.email.isRequired}
                  value={form.email.value}
                  isReadOnly
                  labelPosition="top"
                />
                <TextField
                  width="100%"
                  label="Your Name"
                  type="text"
                  id={form.name.id}
                  isRequired={form.name.isRequired}
                  isDisabled={profileLoading}
                  value={form.name.value}
                  isReadOnly
                />
                <RadioGroup
                  width="100%"
                  id={form.category.id}
                  name={form.category.id}
                  label="Why are you reporting this product?"
                  orientation="horizontal"
                  isRequired={form.category.isRequired}
                  necessityIndicator="icon"
                  isInvalid={!!form.category.errorMessage}
                  value={form.category.value}
                  onChange={(value) => onFormChange(form.category.id, value)}
                  isEmphasized="true"
                  aria-invalid={!!form.category.errorMessage}
                  aria-errormessage="radio-error"
                  errorMessage={form.category.errorMessage}
                >
                  <Radio ref={firstRadioRef} value="DEFAMATION" width="200px">
                    Defamation
                  </Radio>
                  <Radio value="TI" width="200px">
                    Trademark Infringement
                  </Radio>
                  <Radio value="OC" width="200px">
                    Offensive Content
                  </Radio>
                  <Radio value="RHC" width="200px">
                    Racist or Hate Content
                  </Radio>
                  <Radio value="SEC" width="200px">
                    Sexually Explicit Content
                  </Radio>
                  <Radio value="IR" width="200px">
                    Issue with a review
                  </Radio>
                </RadioGroup>
                <TextArea
                  ref={descriptionInputRef}
                  width="100%"
                  label="Please provide a description of your concern."
                  isRequired={form.description.isRequired}
                  value={form.description.value}
                  onChange={(value) => onFormChange(form.description.id, value)}
                  errorMessage={form.description.errorMessage}
                  validationState={form.description.valid}
                  type="text"
                  isDisabled={profileLoading}
                />
              </Flex>
              <Text>
                To report a Copyright Violation, please follow Section 19 in the
                <Link marginStart="size-40" variant="secondary">
                  <a href="https://www.adobe.com/legal/terms.html" target="_blank" rel="noopener noreferrer">
                    Terms of Use
                  </a>
                </Link>
              </Text>
            </Content>
            <Footer>{sendingReportAbuseErrored && <RedText>Failed to send Report Abuse</RedText>}</Footer>
          </Dialog>
        )}
      </DialogContainer>
    </>
  );
};

export default ReportAbuse;
